import React, { useEffect, useState } from 'react';
import { CalendarClock, CheckCircle2, ArrowRight } from 'lucide-react';
import { FONT, MONO } from './ui';

// Flips once at `at` (no per-second re-render of the page).
export const useUnlocked = (at) => {
  const [open, setOpen] = useState(() => Date.now() >= at);
  useEffect(() => {
    if (open) return undefined;
    let id;
    const tick = () => { if (Date.now() >= at) setOpen(true); else id = setTimeout(tick, Math.min(at - Date.now(), 60000)); };
    tick();
    return () => clearTimeout(id);
  }, [open, at]);
  return open;
};

const useLeft = (at) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return Math.max(0, at - now);
};

const TXT = {
  it: { eyebrow: 'The Sound of Authority', msg: 'Il seminario sarà disponibile a partire dalle 14:30 del 4 novembre 2026.', units: ['giorni', 'ore', 'minuti', 'secondi'], signup: 'Iscriviti o accedi', ready: 'Sei iscritto: la pagina si aprirà da sola, non serve ricaricare.', hide: 'Torna al countdown' },
  en: { eyebrow: 'The Sound of Authority', msg: 'The seminar will be available from 14:30 (CET) on 4 November 2026.', units: ['days', 'hours', 'minutes', 'seconds'], signup: 'Sign up or log in', ready: 'You are registered: the page will open by itself, no need to reload.', hide: 'Back to the countdown' },
};

const split = (ms) => { const s = Math.floor(ms / 1000); return [Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60]; };

export const LectureCountdown = ({ at, language, signedIn, onSignup }) => {
  const t = TXT[language] || TXT.it;
  const left = useLeft(at);
  return (
    <section className="min-h-[calc(100vh-4rem)] bg-[#040919] text-white flex items-center relative overflow-hidden" data-testid="lecture-countdown">
      <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 w-full">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-300" style={MONO}><CalendarClock className="w-4 h-4" /> {t.eyebrow}</p>
        <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl" style={FONT} data-testid="lecture-countdown-message">{t.msg}</h1>
        <div className="mt-12 grid grid-cols-4 gap-3 sm:gap-5 max-w-3xl" data-testid="lecture-countdown-timer">
          {split(left).map((v, i) => (
            <div key={t.units[i]} className="rounded-2xl bg-white/5 border border-white/10 px-2 py-5 sm:py-7 text-center">
              <p className="text-4xl sm:text-6xl font-bold tabular-nums" style={MONO} data-testid={`countdown-${['days', 'hours', 'minutes', 'seconds'][i]}`}>{String(v).padStart(2, '0')}</p>
              <p className="mt-2 text-[10px] sm:text-xs uppercase tracking-widest text-blue-200/70">{t.units[i]}</p>
            </div>
          ))}
        </div>
        <div className="mt-12">
          {signedIn ? (
            <p className="flex items-center gap-2 text-sm text-emerald-300" data-testid="lecture-countdown-registered"><CheckCircle2 className="w-4 h-4" /> {t.ready}</p>
          ) : (
            <button onClick={onSignup} className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-slate-950 font-bold hover:bg-amber-300 transition-colors" data-testid="lecture-countdown-signup">
              {t.signup} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export const CountdownBack = ({ language, onClick }) => (
  <button onClick={onClick} className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[300] px-4 py-2 rounded-full bg-[#060d24] text-white text-xs font-semibold shadow-2xl hover:bg-blue-700 transition-colors" data-testid="lecture-countdown-back">
    {(TXT[language] || TXT.it).hide}
  </button>
);
